<template>
  <div class="bg-white rounded-lg border border-border">
    <!-- Card Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border">
      <div class="flex items-center gap-2">
        <BookOpen class="w-4 h-4 text-muted-foreground" />
        <h3 class="text-sm font-semibold text-gray-800">Tuition Status</h3>
      </div>
      <span
        v-if="!isLoading"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
        :class="statusClasses"
      >
        <div class="w-1.5 h-1.5 rounded-full" :class="statusDotClasses"></div>
        {{ statusText }}
      </span>
      <div v-else class="h-6 w-20 animate-pulse bg-gray-100 rounded-full" />
    </div>

    <!-- Card Body -->
    <div class="px-6 py-5">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center py-10">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-cosmicPurple"></div>
      </div>

      <!-- No Tuition -->
      <SubscriptionStatusEmpty
        v-else-if="!props.subscription"
        :plan-source="planSource"
        @subscribe="$emit('subscribe')"
      />

      <!-- Active Subscription -->
      <SubscriptionStatusActive
        v-else
        :subscription="props.subscription"
        :plan-source="planSource"
        :action-loading="actionLoading"
        :action-error="actionError"
        @subscribe="$emit('subscribe')"
        @restore-subscription="$emit('restore-subscription', $event)"
        @reactivate-subscription="$emit('reactivate-subscription', $event)"
        @retry-payment="$emit('retry-payment', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { BookOpen } from 'lucide-vue-next'
import type { Plan, Subscription } from '@/types/subscription'
import {
  resolvePlan,
  getStatusLabel,
  getStatusColor
} from '@/components/subscription/SubscriptionStatus/helpers'
import SubscriptionStatusEmpty from './SubscriptionStatusEmpty.vue'
import SubscriptionStatusActive from './SubscriptionStatusActive.vue'

interface Props {
  subscription?: Subscription | null
  assignedPlan?: Plan | null
  isLoading?: boolean
  actionLoading?: boolean
  actionError?: string | null
}

const props = defineProps<Props>()

defineEmits<{
  subscribe: []
  'reactivate-subscription': [subscription: Subscription]
  'restore-subscription': [subscription: Subscription]
  'retry-payment': [subscription: Subscription]
}>()

const planSource = computed<Plan | null>(() => resolvePlan(props.subscription, props.assignedPlan ?? null))
const statusText = computed(() => getStatusLabel(props.subscription))
const statusColor = computed(() => getStatusColor(props.subscription))

const COLOR_CLASSES: Record<string, string> = {
  green: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  blue: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  yellow: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  gray: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200',
  orange: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200'
}

const DOT_CLASSES: Record<string, string> = {
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
  orange: 'bg-orange-500'
}

const statusClasses = computed(() => COLOR_CLASSES[statusColor.value] || COLOR_CLASSES.gray)
const statusDotClasses = computed(() => DOT_CLASSES[statusColor.value] || DOT_CLASSES.gray)
</script>
